/**
 * Constrained rich-text model. This is what is stored in the template — never HTML.
 * It is intentionally small: paragraphs and lists containing inline runs with a
 * fixed set of marks, plus inline data tokens.
 */

export interface TextMarks {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strike?: boolean;
  code?: boolean;
  /** Colour literal or theme token. */
  color?: string;
  /** A hyperlink. Only `http(s):` and `mailto:` are rendered. May contain `{{ }}` tokens. */
  link?: string;
}

export interface TextRun {
  type: "text";
  text: string;
  marks?: TextMarks;
}

export interface TokenRun {
  type: "token";
  /** Expression, e.g. `client.firstName | upper`. */
  expr: string;
  marks?: TextMarks;
  /** Filled by the resolver: evaluated display value. */
  value?: string;
  /** Filled by the resolver: human label for design-mode chips. */
  label?: string;
  /** Filled by the resolver: expression depends on `page.*` and is evaluated per page. */
  deferred?: boolean;
}

export interface BreakRun {
  type: "break";
}

export type InlineNode = TextRun | TokenRun | BreakRun;

export interface Paragraph {
  type: "paragraph";
  content: InlineNode[];
  align?: "left" | "center" | "right" | "justify";
}

export interface ListItem {
  type: "listItem";
  content: Paragraph[];
}

export interface List {
  type: "bulletList" | "orderedList";
  content: ListItem[];
}

export type BlockNode = Paragraph | List;

export interface RichTextDoc {
  type: "doc";
  content: BlockNode[];
}

export function createParagraph(text = "", marks?: TextMarks): Paragraph {
  return { type: "paragraph", content: text ? [{ type: "text", text, ...(marks ? { marks } : {}) }] : [] };
}

export function createRichText(text = ""): RichTextDoc {
  return { type: "doc", content: [createParagraph(text)] };
}

/** Builds a rich text document from a template string containing `{{ expr }}` tokens. */
export function richTextFromTemplate(template: string): RichTextDoc {
  const paragraphs = template.split(/\n/);
  return {
    type: "doc",
    content: paragraphs.map((p) => ({ type: "paragraph", content: inlineFromTemplate(p) }))
  };
}

export function inlineFromTemplate(text: string): InlineNode[] {
  const out: InlineNode[] = [];
  const re = /\{\{\s*([^}]+?)\s*\}\}/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push({ type: "text", text: text.slice(last, m.index) });
    out.push({ type: "token", expr: m[1]! });
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push({ type: "text", text: text.slice(last) });
  return out;
}

/** Plain-text projection used for search, validation messages and layer labels. */
export function richTextToPlain(doc: RichTextDoc | undefined, tokenText: (expr: string) => string = (e) => `{{${e}}}`): string {
  if (!doc) return "";
  const lines: string[] = [];
  for (const block of doc.content) {
    if (block.type === "paragraph") lines.push(inlineToPlain(block.content, tokenText));
    else for (const item of block.content) for (const p of item.content) lines.push(inlineToPlain(p.content, tokenText));
  }
  return lines.join("\n");
}

export function inlineToPlain(content: InlineNode[], tokenText: (expr: string) => string): string {
  return content
    .map((n) => (n.type === "text" ? n.text : n.type === "token" ? tokenText(n.expr) : "\n"))
    .join("");
}

/** Every token expression used in a rich text document, in order. */
export function richTextTokens(doc: RichTextDoc | undefined): string[] {
  const out: string[] = [];
  if (!doc) return out;
  const visitInline = (content: InlineNode[]) => {
    for (const n of content) {
      if (n.type === "token") out.push(n.expr);
      if (n.type === "text" && n.marks?.link) {
        for (const e of extractTemplateTokens(n.marks.link)) out.push(e);
      }
    }
  };
  for (const block of doc.content) {
    if (block.type === "paragraph") visitInline(block.content);
    else for (const item of block.content) for (const p of item.content) visitInline(p.content);
  }
  return out;
}

/** Extracts `{{ expr }}` expressions from a plain template string. */
export function extractTemplateTokens(text: string | undefined): string[] {
  if (!text) return [];
  const out: string[] = [];
  const re = /\{\{\s*([^}]+?)\s*\}\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) out.push(m[1]!);
  return out;
}

export function isRichTextDoc(value: unknown): value is RichTextDoc {
  return !!value && typeof value === "object" && (value as RichTextDoc).type === "doc" && Array.isArray((value as RichTextDoc).content);
}
